import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEnvelopeOpenText } from "react-icons/fa";
import { AppCard, AppButton, Badge, IconBox } from "../app";
import { appColors, appType } from "../../utils/appTheme";
import { asambleaPropietario } from "../../utils/asambleas/api";

/**
 * Domicilio electrónico del propietario: con él, la citación por WhatsApp o
 * email vale como citación (sin él, puede llegar en papel). Se constituye para
 * el contacto actual; si cambia el celular o el email, hay que volver a hacerlo.
 * `style`: el ancho que le da la pantalla (en desktop, una columna angosta).
 */
export default function DomicilioElectronicoCard({ style } = {}) {
  const { t } = useTranslation("asambleas");
  const [info, setInfo] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [canal, setCanal] = useState("whatsapp");
  const [acepto, setAcepto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    try {
      setInfo(await asambleaPropietario("domicilio", { accion: "ver" }));
    } catch (_) {
      setInfo(null); // sin módulo o sin unidades como propietario: no se muestra
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (!info || !info.domicilios || !info.domicilios.length) return null;
  const d = info.domicilios[0];
  const contactos = d.contactos || {};

  const enviar = async (accion) => {
    setGuardando(true);
    setError("");
    try {
      setInfo(await asambleaPropietario("domicilio", accion === "constituir" ? { accion, canal, acepto } : { accion }));
      setAbierto(false);
      setAcepto(false);
    } catch (e) {
      setError(e && e.message ? String(e.message).replace(/^FirebaseError:\s*/, "") : t("domicilio.error"));
    } finally {
      setGuardando(false);
    }
  };

  const estado = !d.constituido
    ? { tono: "amber", texto: t("domicilio.estadoNo") }
    : d.vigente
      ? { tono: "green", texto: t("domicilio.estadoSi", { canal: t(`domicilio.canal.${d.canal}`) }) }
      : { tono: "red", texto: t("domicilio.estadoVencido") };

  return (
    <AppCard padding={16} style={style}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <IconBox icon={<FaEnvelopeOpenText />} color={appColors.blue} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...appType.cardTitle, fontSize: 15 }}>{t("domicilio.titulo")}</div>
          <div style={{ ...appType.body, fontSize: 13, color: appColors.ink3, marginTop: 4 }}>{t("domicilio.descripcion")}</div>
        </div>
        <Badge tone={estado.tono} dot>{estado.texto}</Badge>
      </div>

      {!abierto ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          {(!d.constituido || !d.vigente) && (
            <AppButton variant="primary" onClick={() => setAbierto(true)}>{t("domicilio.constituir")}</AppButton>
          )}
          {d.constituido && (
            <AppButton variant="ghost" disabled={guardando} onClick={() => enviar("revocar")}>{t("domicilio.revocar")}</AppButton>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          <div role="radiogroup" aria-label={t("domicilio.elegirCanal")} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {["whatsapp", "email"].map((c) => (
              <label key={c} style={{ ...appType.body, display: "flex", alignItems: "center", gap: 8, opacity: contactos[c] ? 1 : 0.5 }}>
                <input type="radio" name="canal-domicilio" value={c} checked={canal === c} disabled={!contactos[c]}
                  onChange={() => setCanal(c)} />
                {t(`domicilio.canal.${c}`)} {contactos[c] ? `(${contactos[c]})` : `— ${t("domicilio.sinContacto")}`}
              </label>
            ))}
          </div>
          <div style={{ ...appType.body, fontSize: 13, color: appColors.ink2, background: appColors.cream, padding: 12, borderRadius: 12 }}>
            {info.texto ? info.texto.texto : ""}
          </div>
          <label style={{ ...appType.body, display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
            {t("domicilio.acepto")}
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <AppButton variant="primary" disabled={!acepto || guardando || !contactos[canal]} onClick={() => enviar("constituir")}>
              {guardando ? t("domicilio.guardando") : t("domicilio.confirmar")}
            </AppButton>
            <AppButton variant="ghost" onClick={() => setAbierto(false)}>{t("domicilio.cancelar")}</AppButton>
          </div>
        </div>
      )}
      {error && <div role="alert" style={{ ...appType.body, color: appColors.red, marginTop: 8 }}>{error}</div>}
    </AppCard>
  );
}
